import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { USPS, WHY_CHOOSE, RECENT_WORK } from "@/lib/data";

const heroImage = "/home/hero.jpg";

// Two product cards — only two brands/categories now
const OFFERINGS = [
  {
    id: "terrazzo",
    number: "01",
    brand: "Terrazzo India",
    name: "Precast Terrazzo & Cast-on-site",
    blurb:
      "Cast, cured and finished in-house. Precast slabs and tiles, plus seamless cast-on-site floors — custom aggregate, custom size, custom colour.",
    image: "/home/pink-marble-round.jpg",
    href: "/products",
    cta: "Browse the catalogue",
  },
  {
    id: "bricks",
    number: "02",
    brand: "Cement Bricks",
    name: "Cement Cladding Bricks",
    blurb:
      "Premium high-density cement cladding bricks engineered for compressive strength and visual consistency. Architectural greys, naturals, and bespoke pigment runs.",
    image: "/home/bricks-card.jpg",
    href: "/bricks",
    cta: "Browse the brick catalogue",
  },
];

export default function Home() {
  const [activeWork, setActiveWork] = useState(0);

  useEffect(() => {}, []);

  return (
    <div data-testid="home-page">
      {/* ====== HORIZONTAL HERO BANNER ====== */}
      <section data-testid="home-hero" className="hero-banner">
        <img src={heroImage} alt="Terrazzo surface detail" />
        <div className="grain" />
        <div className="absolute inset-0 flex items-end z-10">
          <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20 pb-8 sm:pb-16 md:pb-24 pt-6 sm:pt-0">
            <p data-testid="hero-eyebrow" className="font-mono-label mb-3 sm:mb-6 reveal reveal-1" style={{color: "rgba(243,236,224,0.8)"}}>
              A Yogii Industries brand · Vasai · India · Est. 1999
            </p>
            <h1
              className="font-serif-display text-[2rem] sm:text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight reveal reveal-2 max-w-6xl"
              style={{color: "#f3ece0"}}
            >
              What you <em className="italic text-terracotta">imagine</em> is exactly what gets delivered.
            </h1>
            <div className="mt-7 sm:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6 sm:gap-8 reveal reveal-3">
              <p className="max-w-xl text-sm sm:text-base md:text-lg leading-relaxed" style={{color: "rgba(243,236,224,0.88)"}}>
                Terrazzo, made the long way. Custom slabs, tiles, and cement cladding bricks — straight from our studio floor to yours. No agents, no brokers, no compromises.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/request-quote"
                  data-testid="hero-cta-quote"
                  className="inline-flex items-center font-mono-label px-5 sm:px-7 py-3.5 sm:py-4 bg-terracotta hover:bg-cream hover:text-ink transition-colors"
                  style={{color: "#f3ece0"}}
                >
                  Request a Sample →
                </Link>
                <Link
                  to="/products"
                  data-testid="hero-cta-products"
                  className="inline-flex items-center font-mono-label px-5 sm:px-7 py-3.5 sm:py-4 border hover:bg-cream hover:text-ink transition-colors"
                  style={{color: "#f3ece0", borderColor: "rgba(243,236,224,0.6)"}}
                >
                  Explore Catalogue
                </Link>
              </div>
            </div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 reveal reveal-4">
          <span className="font-mono-label" style={{color: "rgba(243,236,224,0.55)"}}>scroll</span>
          <span className="w-px h-10 bg-cream/40" />
        </div>
      </section>

      {/* ====== MARQUEE ====== */}
      <section className="border-y border-warm bg-stone py-6 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...USPS, ...USPS, ...USPS].map((u, i) => (
            <span key={i} className="font-mono-label text-stone-text mx-10 inline-flex items-center gap-10">
              {u}<span className="w-2 h-2 rounded-full bg-terracotta" />
            </span>
          ))}
        </div>
      </section>

      {/* ====== 01 — WHAT WE MAKE (TWO OFFERINGS) ====== */}
      <section data-testid="home-products" className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="col-span-12 md:col-span-4">
              <p className="font-mono-label text-terracotta">01 — What we make</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05] max-w-3xl">
                Two surfaces. <em className="italic text-terracotta">One</em> standard of finish.
              </h2>
              <p className="mt-5 sm:mt-6 text-sm sm:text-base text-stone-text max-w-2xl leading-relaxed">
                A focused portfolio — engineered, not catalogued. Every piece is built to your drawing, fabricated in our own facility, and inspected by a senior before it leaves.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-10">
            {OFFERINGS.map((o) => (
              <Link
                key={o.id}
                to={o.href}
                data-testid={`home-offering-${o.id}`}
                className="group block bg-stone relative overflow-hidden"
              >
                <div className="image-zoom-wrap aspect-[5/4]">
                  <img src={o.image} alt={o.name} className="w-full h-full object-cover" />
                </div>
                <div className="absolute top-4 left-4 sm:top-5 sm:left-5 md:top-7 md:left-7 flex items-center gap-2 sm:gap-3">
                  <span className="font-mono-label px-2.5 py-1 sm:px-3 sm:py-1.5 bg-cream/95 backdrop-blur text-ink">
                    {o.number}
                  </span>
                  <span className="font-mono-label px-2.5 py-1 sm:px-3 sm:py-1.5 bg-ink/90 backdrop-blur text-cream">
                    {o.brand}
                  </span>
                </div>
                <div className="p-6 sm:p-7 md:p-10">
                  <h3 className="font-serif-display text-2xl sm:text-3xl md:text-5xl leading-[1.05] group-hover:text-terracotta transition-colors">
                    {o.name}
                  </h3>
                  <p className="mt-4 sm:mt-5 text-sm sm:text-base text-stone-text leading-relaxed max-w-md">{o.blurb}</p>
                  <p className="mt-6 sm:mt-7 font-mono-label inline-flex items-center gap-2 border-b border-ink pb-1 group-hover:text-terracotta group-hover:border-terracotta transition-colors">
                    {o.cta} →
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ====== 02 — TWO BRANDS, ONE VISION (Yogii Industries) ====== */}
      <section data-testid="home-philosophy" className="bg-ink text-cream py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20">
          <div className="grid grid-cols-12 gap-8 sm:gap-10 lg:gap-12 lg:items-end mb-8 sm:mb-12">
            <div className="col-span-12 lg:col-span-5">
              <p className="font-mono-label mb-5 sm:mb-6" style={{color: "rgba(243,236,224,0.55)"}}>
                02 — The people behind the product
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.0]">
                Two brands,<br /><em className="italic" style={{color: "#c8ac7b"}}>one</em> vision.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <p className="text-base sm:text-lg leading-relaxed max-w-2xl" style={{color: "rgba(243,236,224,0.82)"}}>
                <span className="font-serif-display italic text-cream">Yogii Industries</span> houses two
                distinguished manufacturing brands, each a master of its craft, united by an unwavering
                commitment to beauty and durability.
              </p>
            </div>
          </div>

          {/* The two brand cards */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-10 sm:mb-14">
            {/* Terrazzo India */}
            <div data-testid="brand-card-terrazzo" className="relative border border-cream/15 p-7 sm:p-8 md:p-12 group hover:border-terracotta transition-colors">
              <p className="font-mono-label text-terracotta mb-5 sm:mb-6">Brand 01</p>
              <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4">
                Terrazzo <em className="italic">India</em>
              </h3>
              <p className="font-mono-label mb-6 sm:mb-8" style={{color: "rgba(243,236,224,0.55)"}}>
                Surface · Slabs · Tiles
              </p>
              <p className="text-sm sm:text-base leading-relaxed mb-6 sm:mb-8" style={{color: "rgba(243,236,224,0.78)"}}>
                Handmade terrazzo, finished by senior craftsmen. Custom aggregates, fully bespoke compositions,
                samples in days. The brand you are reading right now.
              </p>
              <Link
                to="/products"
                data-testid="brand-card-terrazzo-cta"
                className="font-mono-label inline-flex items-center gap-2 border-b border-cream/40 pb-1 group-hover:text-terracotta group-hover:border-terracotta transition-colors"
              >
                Explore the catalogue →
              </Link>
            </div>

            {/* Cement Bricks */}
            <div data-testid="brand-card-mattoni" className="relative border border-cream/15 p-7 sm:p-8 md:p-12 group hover:border-terracotta transition-colors">
              <p className="font-mono-label text-terracotta mb-5 sm:mb-6">Brand 02</p>
              <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4">
                Cement <em className="italic">Bricks</em>
              </h3>
              <p className="font-mono-label mb-6 sm:mb-8" style={{color: "rgba(243,236,224,0.55)"}}>
                Structure · Cement Cladding Bricks
              </p>
              <p className="text-sm sm:text-base leading-relaxed mb-6 sm:mb-8" style={{color: "rgba(243,236,224,0.78)"}}>
                Premium cement cladding bricks engineered for compressive strength, dimensional consistency, and a
                clean architectural face. From core walls to feature facades.
              </p>
              <Link
                to="/bricks"
                data-testid="brand-card-mattoni-cta"
                className="font-mono-label inline-flex items-center gap-2 border-b border-cream/40 pb-1 group-hover:text-terracotta group-hover:border-terracotta transition-colors"
              >
                Explore the brick catalogue →
              </Link>
            </div>
          </div>

          {/* USP strip — what binds the two brands */}
          <div className="border-t border-cream/15 pt-10 sm:pt-12">
            <p className="font-mono-label mb-6 sm:mb-8" style={{color: "rgba(243,236,224,0.55)"}}>What ties them together</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-5">
              {USPS.map((u) => (
                <div key={u} className="border-t border-cream/15 pt-3 sm:pt-4">
                  <span className="block w-1.5 h-1.5 bg-terracotta mb-3" />
                  <p className="text-xs sm:text-sm leading-snug" style={{color: "rgba(243,236,224,0.82)"}}>{u}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== 03 — WHY CHOOSE TERRAZZO INDIA ====== */}
      <section data-testid="home-why-choose" className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-cream">
        <div className="max-w-[1600px] mx-auto">
          {/* Top: What Makes Us Different */}
          <div className="grid grid-cols-12 gap-8 sm:gap-10 lg:gap-16 mb-10 sm:mb-14 md:mb-16">
            <div className="col-span-12 md:col-span-4">
              <p className="font-mono-label text-terracotta mb-4">03 — Why choose Terrazzo India</p>
              <p className="font-mono-label text-stone-text">{WHY_CHOOSE.eyebrow}</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
                Two things we always keep in mind —{" "}
                <em className="italic text-terracotta">Versatility</em>.{" "}
                <em className="italic text-terracotta">Value</em>.
              </h2>
              <p className="mt-6 sm:mt-8 text-sm sm:text-base md:text-lg text-stone-text leading-relaxed max-w-2xl">
                {WHY_CHOOSE.copy}
              </p>
            </div>
          </div>

          {/* People + Serve grid */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-10 sm:mb-14">
            <div className="bg-stone p-7 sm:p-10 md:p-12 border-l-4 border-terracotta">
              <p className="font-mono-label text-terracotta mb-5 sm:mb-6">{WHY_CHOOSE.team.eyebrow}</p>
              <p className="text-sm sm:text-base text-stone-text leading-relaxed">{WHY_CHOOSE.team.body}</p>
            </div>
            <div className="bg-ink text-cream p-7 sm:p-10 md:p-12">
              <p className="font-mono-label mb-5 sm:mb-6" style={{color: "rgba(243,236,224,0.6)"}}>
                {WHY_CHOOSE.serve.eyebrow}
              </p>
              <ul className="space-y-4 sm:space-y-5">
                {WHY_CHOOSE.serve.items.map((s, i) => (
                  <li key={i} className="flex gap-4 items-baseline">
                    <span className="font-mono-label text-terracotta shrink-0">0{i + 1}</span>
                    <p className="text-sm sm:text-base leading-relaxed" style={{color: "rgba(243,236,224,0.85)"}}>{s}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Our USP */}
          <div className="border-t border-warm pt-10 sm:pt-14">
            <p className="font-mono-label text-terracotta mb-6 sm:mb-8 text-center">{WHY_CHOOSE.usp.eyebrow}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5 md:gap-6">
              {WHY_CHOOSE.usp.items.map((u, i) => (
                <div key={i} className="text-center py-6 sm:py-8 px-3 border border-warm hover:border-terracotta transition-colors group">
                  <span className="font-serif-display text-2xl sm:text-3xl text-terracotta">✓</span>
                  <p className="mt-2 sm:mt-3 text-xs sm:text-sm leading-snug">{u}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 sm:mt-14 text-center font-serif-display italic text-lg sm:text-xl md:text-2xl lg:text-3xl text-ink max-w-4xl mx-auto leading-snug px-4">
              When you call <span className="not-italic font-normal text-terracotta">Terrazzo India</span>, you get <span className="not-italic font-normal text-terracotta">Terrazzo India</span> — the ones who cast it, deliver it, and stand behind every square metre.
              <br className="hidden sm:block" /> No agents. No brokers. No surprises.
            </p>
          </div>
        </div>
      </section>

      {/* ====== 04 — RECENT WORK ====== */}
      <section data-testid="home-gallery-preview" className="bg-cream py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="px-4 sm:px-6 md:px-16 lg:px-20 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="col-span-12 md:col-span-4">
              <p className="font-mono-label text-terracotta">04 — Recent Work</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
                A few of the <em className="italic text-terracotta">latest</em> commissions.
              </h2>
              <p className="mt-5 sm:mt-6 text-sm sm:text-base md:text-lg text-stone-text max-w-2xl leading-relaxed">
                Every piece below was cast, cured and installed by our own team. Same drawing → same finish, every time.
              </p>
              <Link
                to="/work"
                data-testid="home-see-all-work"
                className="inline-flex mt-5 sm:mt-6 font-mono-label items-center gap-2 border-b border-ink pb-1 hover:text-terracotta hover:border-terracotta transition-colors"
              >
                See all work →
              </Link>
            </div>
          </div>
        </div>

        {/* Featured project (large) */}
        <div className="px-4 sm:px-6 md:px-16 lg:px-20 max-w-[1600px] mx-auto">
          <div className="relative image-zoom-wrap aspect-[4/5] sm:aspect-[16/10] lg:aspect-[16/8] mb-6 sm:mb-8">
            <img
              src={RECENT_WORK[activeWork].image}
              alt={RECENT_WORK[activeWork].title}
              className="w-full h-full object-cover"
              key={RECENT_WORK[activeWork].id}
            />
            <div className="absolute inset-0 pointer-events-none" style={{background: "linear-gradient(180deg, transparent 55%, rgba(26,23,20,0.75) 100%)"}} />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-12 text-cream flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-6">
              <div>
                <p className="font-mono-label mb-2 sm:mb-3" style={{color: "rgba(243,236,224,0.75)"}}>
                  {String(activeWork + 1).padStart(2, "0")} / {String(RECENT_WORK.length).padStart(2, "0")} · {RECENT_WORK[activeWork].category}
                </p>
                <h3 className="font-serif-display text-2xl sm:text-4xl md:text-6xl leading-[1.05]">
                  {RECENT_WORK[activeWork].title}
                </h3>
              </div>
              <p className="text-xs sm:text-sm max-w-xs sm:text-right" style={{color: "rgba(243,236,224,0.85)"}}>
                {RECENT_WORK[activeWork].meta}
              </p>
            </div>
          </div>

          {/* Thumbnail strip */}
          <div
            data-testid="home-work-thumbs"
            className="grid grid-cols-5 gap-2 sm:gap-3 md:gap-4"
          >
            {RECENT_WORK.map((w, i) => (
              <button
                key={w.id}
                data-testid={`work-thumb-${i}`}
                onClick={() => setActiveWork(i)}
                onMouseEnter={() => setActiveWork(i)}
                aria-label={`View ${w.title}`}
                className={`group image-zoom-wrap aspect-square relative overflow-hidden transition-all duration-300 focus:outline-none ${
                  i === activeWork ? "ring-2 ring-terracotta ring-offset-2 ring-offset-cream" : "opacity-70 hover:opacity-100"
                }`}
              >
                <img src={w.image} alt={w.title} loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 font-mono-label text-cream text-[0.5rem] sm:text-[0.6rem] px-1 sm:px-1.5 py-0.5 bg-ink/70 backdrop-blur">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ====== 04 — HAVE A PROJECT IN MIND? ====== */}
      <section data-testid="home-final-cta" className="bg-cream-2 py-12 sm:py-16 md:py-20 relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20 grid grid-cols-12 gap-8 sm:gap-10 lg:items-center">
          <div className="col-span-12 lg:col-span-7">
            <p className="font-mono-label text-terracotta mb-5 sm:mb-6">05 — Let's begin</p>
            <h2 className="font-serif-display text-[2rem] sm:text-5xl md:text-7xl lg:text-8xl leading-[0.98]">
              Have a project<br />
              in <em className="italic text-terracotta">mind?</em>
            </h2>
            <p className="mt-7 sm:mt-10 text-base sm:text-lg md:text-xl leading-relaxed text-stone-text max-w-2xl">
              We work directly with architects, interior designers, contractors, and developers. Let's discuss your next surface requirement —{" "}
              <span className="text-ink italic font-serif-display">no minimums too small, no vision too large.</span>
            </p>

            <div className="mt-9 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/request-quote"
                data-testid="final-cta-quote"
                className="inline-flex justify-center font-mono-label px-6 sm:px-8 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors"
              >
                Start an enquiry →
              </Link>
              <Link
                to="/contact"
                data-testid="final-cta-contact"
                className="inline-flex justify-center font-mono-label px-6 sm:px-8 py-3.5 sm:py-4 border border-ink text-ink hover:bg-ink hover:text-cream transition-colors"
              >
                Talk to the studio
              </Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4 md:gap-5">
            {[
              { k: "Working with", v: "Architects" },
              { k: "Working with", v: "Interior designers" },
              { k: "Working with", v: "Contractors" },
              { k: "Working with", v: "Developers" },
            ].map((c) => (
              <div key={c.v} className="border-t-2 border-ink/80 pt-3 sm:pt-4">
                <p className="font-mono-label text-stone-text">{c.k}</p>
                <p className="font-serif-display text-xl sm:text-2xl md:text-3xl mt-2 leading-tight">{c.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
